import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ChildCheckInCreatedResponse } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { kidsApi } from '@/services/kids.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field } from '@/components/ui';

/**
 * Checks a child in.
 *
 * Two phases: the form, then — on success — the code itself, shown large
 * and printable. It never appears anywhere else; there is no "view code
 * again" affordance anywhere in the product, on purpose.
 */
export function CheckInDialog({
  onClose,
  onCheckedIn,
}: {
  onClose: () => void;
  onCheckedIn: () => void;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<ChildCheckInCreatedResponse | null>(null);

  const [childId, setChildId] = useState<string | null>(null);
  const [childLabel, setChildLabel] = useState('');
  const [guardianId, setGuardianId] = useState<string | null>(null);
  const [guardianLabel, setGuardianLabel] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const mutation = useMutation({
    mutationFn: () =>
      kidsApi.checkIn({
        childPersonId: childId!,
        guardianDropOffPersonId: guardianId,
        note: note.trim() || null,
      }),
    onSuccess: setResult,
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError
          ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
          : t('errors.INTERNAL_ERROR'),
      );
    },
  });

  if (result) {
    return (
      <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="code-title">
        <div className="dialog-body result-panel">
          <h2 id="code-title">{t('kids.checkin.codeTitle')}</h2>
          <p className="auth-subtitle">
            {t('kids.checkin.codeHint', { values: { child: result.childDisplayName } })}
          </p>
          <p className="kids-code" dir="ltr">
            {result.securityCode}
          </p>
          <Button
            variant="primary"
            onClick={() => {
              onCheckedIn();
            }}
          >
            {t('common.actions.close')}
          </Button>
        </div>
      </dialog>
    );
  }

  return (
    <dialog ref={dialogRef} onClose={onClose} className="dialog" aria-labelledby="check-in-title">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setFormError(null);
          mutation.mutate();
        }}
        noValidate
        className="dialog-body"
      >
        <h2 id="check-in-title">{t('kids.checkin.checkIn')}</h2>

        {formError && <Alert tone="danger">{formError}</Alert>}

        <PersonPicker
          label={t('kids.checkin.child')}
          value={childLabel}
          selectedId={childId}
          onChange={setChildLabel}
          onPick={(id, label) => {
            setChildId(id);
            setChildLabel(label);
          }}
        />

        <PersonPicker
          label={t('kids.checkin.guardianDropOff')}
          value={guardianLabel}
          selectedId={guardianId}
          onChange={setGuardianLabel}
          onPick={(id, label) => {
            setGuardianId(id);
            setGuardianLabel(label);
          }}
          optional
        />

        <Field
          label={t('kids.checkin.note')}
          name="note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />

        <footer className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={childId === null}
            loading={mutation.isPending}
          >
            {t('kids.checkin.checkIn')}
          </Button>
        </footer>
      </form>
    </dialog>
  );
}

function PersonPicker({
  label,
  value,
  selectedId,
  onChange,
  onPick,
  optional,
}: {
  label: string;
  value: string;
  selectedId: string | null;
  onChange: (value: string) => void;
  onPick: (id: string, label: string) => void;
  optional?: boolean;
}) {
  const { t } = useI18n();
  const query = useQuery({
    queryKey: ['people', 'picker', value],
    queryFn: ({ signal }) => peopleApi.list({ search: value, limit: 8 }, signal),
    enabled: value.trim().length >= 2 && selectedId === null,
  });
  const candidates = (query.data?.data ?? []).filter((person) => !person.archivedAt);

  return (
    <div className="member-picker">
      <Field
        label={label}
        name={`person-search-${label}`}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={t('events.registration.choosePerson')}
        required={!optional}
      />
      {candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button variant="ghost" onClick={() => onPick(person.id, person.displayName)}>
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
